import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import useMutation from "src/hooks/useMutation";
import {
  UPDATE_INVENTORY_ITEM_MUTATION,
  CREATE_INVENTORY_TRANSACTION_MUTATION,
} from "src/constants/graphql/mutations";
import StorageLocationAutocomplete from "src/views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import useNotification from "src/hooks/notification";

export default function StagingModal({ open = true, asset, item, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [location, setLocation] = useState({
    warehouse: null,
    warehouse_locations: [],
    sub_qty: item?.quantity || 0,
  });

  const [updateItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION);
  const [createTransaction] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);
  const { notifySuccess } = useNotification();

  const header = "Order Assistant";
  const question = "Which location are you staging this item in?";

  const updateStatus = async () => {
    await updateItem({
      variables: {
        id: item.id,
        assetId: item.assetId,
        quantity: item.quantity,
        stagedStatus: true,
      },
    });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      const rackLocations = (asset?.rackLocations || []).map(l => ({ ...l }));
      const oldRackLocations = asset?.rackLocations || [];
      const rackLocationsChanges = [
        {
          ...location,
          sub_qty: item?.quantity || 0,
        },
      ];

      if (rackLocations.length) {
        rackLocations[0] = { ...rackLocations[0], ...location, sub_qty: item?.quantity || 0 };
      } else {
        rackLocations.push({ ...location, sub_qty: item?.quantity || 0 });
      }

      await createTransaction({
        variables: {
          code: asset.assetId,
          quantity: 0,
          notes: "",
          senderName: "",
          carrierName: "",
          trackingNumber: "",
          orderNumber: "",
          poNumber: "",
          rackLocations,
          rackLocationsChanges,
          oldRackLocations,
          inventoryItemId: item.id,
          actionSource: "inventory_request_staging",
        },
      });

      await updateStatus();

      notifySuccess("Item staged successfully");

      onSuccess && onSuccess();
      onClose && onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ "& .MuiDialog-paper": { width: 400 } }}
    >
      <DialogTitle>
        <Typography align="center" fontSize={18} fontWeight={500} color="text.primary">
          {header}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box py={4}>
          <Typography align="center" sx={{ mb: 2 }} color="text.primary" fontSize={16}>
            {question}
          </Typography>
          <StorageLocationAutocomplete
            name="location"
            value={location}
            projectId={asset?.project?.id}
            onChange={values => setLocation(prev => ({ ...prev, ...values }))}
            disabled={saving}
            loading={false}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!location?.warehouse?.id || !location?.warehouse_locations?.[0]?.id || saving}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
